import React from 'react';
import { css } from '@emotion/react';
import {
	QUERY_REQUEST_DATA,
	QUERY_REQUEST_STATE,
	MUTATION_UPDATE_DATA_TECH,
} from 'queries/queries-booking';
import { Controller, useForm } from 'react-hook-form';

import { useMutation, useQuery } from 'urql';
import { Button } from '../../../components/Button';
import { FormControl } from '../../../components/FormControl';
import { FormFooter } from '../../../components/FormFooter';
import { FormLine } from '../../../components/FormLine';
import { FormSection } from '../../../components/FormSection';
import { FormSectionHeading } from '../../../components/FormSectionHeading';
import { Heading } from '../../../components/Heading';
import { Input } from '../../../components/Input';
import { emailValidation, nonEmptyValidation, phoneValidation } from '../forms/validations';
import { useStep } from '../hooks/useStep';

import { IconEmail } from 'icons/icon-email';
import { SpinnerBlock } from 'components/SpinnerBlock';
import { useStore } from 'hooks/useStore';

import { format } from 'date-fns';
import { theme } from 'styles/theme';

import { OptionalInfoBox } from '../components/OptionalInfoBox';
import { LinkExternal } from 'components/LinkExternal';
import { PhoneInput } from 'components/PhoneInput';
import { useRequestStateQuery } from 'generated/graphql';

enum FORM_TECH {
	SOUND_NAME = 'tech_sound_name',
	SOUND_EMAIL = 'tech_sound_email',
	SOUND_PHONE = 'tech_sound_phone',

	CONTACT_NAME = 'tech_contact_name',
	CONTACT_EMAIL = 'tech_contact_email',
	CONTACT_PHONE = 'tech_contact_phone',
}

export const TechStep = () => {
	const { id } = useStep();

	const [queryRequestData] = useQuery({
		query: QUERY_REQUEST_DATA,
		variables: {
			url: id,
		},
	});

	const [_stateRequest] = useRequestStateQuery({
		variables: { url: id },
	});

	const timeUpdateAt = queryRequestData?.data?.request?.tech_update_at;

	return (
		<div
			css={css`
				width: 560px;
			`}
		>
			<Heading
				title="Zvuk a technika"
				desc={
					<>
						<OptionalInfoBox />
						{!!timeUpdateAt && queryRequestData?.data?.request?.confirmed && (
							<div
								css={css`
									border: 1px solid #f1f1f4;
									border-radius: 12px;
									background: #fafaff;
									padding: 12px 20px;
									margin-top: 8px;
									text-align: center;
									font-size: 14px;
									line-height: 20px;
									color: ${theme.color.text['400']};
								`}
							>
								<strong
									css={css`
										font-weight: 500;
									`}
								>
									Poslední aktualizace:
								</strong>{' '}
								{format(new Date(timeUpdateAt), 'dd. MM. YYY, HH:mm')}
							</div>
						)}
					</>
				}
			/>

			{queryRequestData.fetching ? (
				<SpinnerBlock />
			) : (
				<Form defaultValues={queryRequestData?.data?.request} url={id} />
			)}
		</div>
	);
};

const Form = ({ defaultValues, url }) => {
	const { notifications } = useStore();

	const { goNextStep, goPrevStep } = useStep();

	const localDefaultValues = {
		tech_sound_name: defaultValues?.tech_sound_name,
		tech_sound_email: defaultValues?.tech_sound_email,
		tech_sound_phone: defaultValues?.tech_sound_phone,

		tech_contact_name: defaultValues?.tech_contact_name,
		tech_contact_email: defaultValues?.tech_contact_email,
		tech_contact_phone: defaultValues?.tech_contact_phone,
	};

	const {
		handleSubmit,
		register,
		control,
		formState: { errors },
	} = useForm({ defaultValues: localDefaultValues });

	// Submit Form
	const [updateDataTech, executeUpdateDataTech] = useMutation(MUTATION_UPDATE_DATA_TECH);

	const onSubmit = (data: any) => {
		// console.log('SUBMIT');

		// todo: tohle je trosku naprasene, ale funguje to, finalne ma byt 12 znaku prozatim, je to i v ubytovani
		if (data.tech_sound_phone !== null && data.tech_sound_phone.length <= 6) {
			data.tech_sound_phone = null;
		}

		if (data.tech_contact_phone !== null && data.tech_contact_phone.length <= 6) {
			data.tech_contact_phone = null;
		}

		// console.log(data);

		executeUpdateDataTech({
			url: url,
			input: data,
		}).then((result) => {
			// console.log(result);

			if (result?.error) {
				// VYHODIT ERROR
				return false;
			} else {
				notifications.addNotification({ message: 'Uloženo' });
			}

			goNextStep();
		});
	};

	const labelWidth = 178;

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FormSection>
				<FormSectionHeading>Zvukař</FormSectionHeading>
				<FormLine first>
					<FormControl
						label="Jméno"
						htmlFor={FORM_TECH.SOUND_NAME}
						labelWidth={labelWidth}
						error={errors[FORM_TECH.SOUND_NAME]}
					>
						<Input
							placeholder="Zadejte celé jméno..."
							{...register(FORM_TECH.SOUND_NAME, nonEmptyValidation({}))}
							error={!!errors[FORM_TECH.SOUND_NAME]}
						/>
					</FormControl>
				</FormLine>
				<FormLine>
					<FormControl
						label="E-mail"
						htmlFor={FORM_TECH.SOUND_EMAIL}
						labelWidth={labelWidth}
						error={errors[FORM_TECH.SOUND_EMAIL]}
					>
						<Input
							icon={<IconEmail />}
							{...register(FORM_TECH.SOUND_EMAIL, emailValidation({}))}
							error={!!errors[FORM_TECH.SOUND_EMAIL]}
						/>
					</FormControl>
				</FormLine>
				<FormLine>
					<FormControl
						label="Telefonní číslo"
						htmlFor={FORM_TECH.SOUND_PHONE}
						labelWidth={labelWidth}
						error={errors[FORM_TECH.SOUND_PHONE]}
					>
						<Controller
							name={FORM_TECH.SOUND_PHONE}
							control={control}
							rules={{
								...phoneValidation(),
							}}
							render={({ field }) => {
								return (
									<PhoneInput {...field} width={240} error={!!errors[FORM_TECH.SOUND_PHONE]} />
								);
							}}
						/>
					</FormControl>
				</FormLine>
				<FormSectionHeading>Technický rider</FormSectionHeading>
				<div
					css={css`
						border: 1px solid #f1f1f4;
						border-radius: 12px;
						background: #fafaff;
						padding: 12px 20px;
						margin-top: 8px;
						text-align: center;
						font-size: 14px;
						line-height: 20px;
						color: ${theme.color.text['400']};
					`}
				>
					<strong
						css={css`
							font-weight: 500;
						`}
					>
						{!!defaultValues?.artist.rider_link ? (
							<>
								Technický rider najdete{' '}
								<LinkExternal href={defaultValues?.artist.rider_link}>zde.</LinkExternal>
							</>
						) : (
							'Technický rider není stodupný. Kontaktujte bookera.'
						)}
					</strong>
				</div>

				<FormSectionHeading>Kontaktní osoba v den akce</FormSectionHeading>

				<FormLine first>
					<FormControl
						label="Jméno"
						htmlFor={FORM_TECH.CONTACT_NAME}
						labelWidth={labelWidth}
						error={errors[FORM_TECH.CONTACT_NAME]}
					>
						<Input
							placeholder="Zadejte celé jméno..."
							{...register(FORM_TECH.CONTACT_NAME, nonEmptyValidation({}))}
							error={!!errors[FORM_TECH.CONTACT_NAME]}
						/>
					</FormControl>
				</FormLine>
				<FormLine>
					<FormControl
						label="E-mail"
						htmlFor={FORM_TECH.CONTACT_EMAIL}
						labelWidth={labelWidth}
						error={errors[FORM_TECH.CONTACT_EMAIL]}
					>
						<Input
							icon={<IconEmail />}
							{...register(FORM_TECH.CONTACT_EMAIL, emailValidation({}))}
							error={!!errors[FORM_TECH.CONTACT_EMAIL]}
						/>
					</FormControl>
				</FormLine>
				<FormLine>
					<FormControl
						label="Telefonní číslo"
						htmlFor={FORM_TECH.CONTACT_PHONE}
						labelWidth={labelWidth}
						error={errors[FORM_TECH.CONTACT_PHONE]}
					>
						<Controller
							name={FORM_TECH.CONTACT_PHONE}
							control={control}
							rules={{
								...phoneValidation(),
							}}
							render={({ field }) => {
								return (
									<PhoneInput {...field} width={240} error={!!errors[FORM_TECH.CONTACT_PHONE]} />
								);
							}}
						/>
					</FormControl>
				</FormLine>
			</FormSection>

			<FormFooter>
				<Button outline type="button" onClick={() => goPrevStep()}>
					Zpět
				</Button>
				<Button type="submit" isProcessing={updateDataTech.fetching}>
					{defaultValues?.confirmed ? 'Aktualizovat a pokračovat' : 'Uložit a pokračovat'}
				</Button>
			</FormFooter>
		</form>
	);
};
